<template>
    <Dialog 
        :visible="props.visible" 
        class="create-folder-dialog"
        :modal="true"
        :close-on-escape="true"
        :aria-expanded="props.visible"
        aria-describedby="Create folder modal"
        :dismissable-mask="true"
        :closable="false"
    >
        <div v-focustrap aria-modal="true" ref="new-folder-dialog-content-ref" class="wrapper">
            <h2 class="title">Create new folder</h2>
            <ErrorBanner
                :message="errorBannerMessage"
                :visible="showErrorBanner"
                @close="setshowErrorBanner(false)"
            />
            <section role="Title input" class="input-wrapper">
                <span class="p-input-icon-right" aria-label="Title input">
                    <InputText
                        autofocus
                        id="newFolder"
                        v-model="newFolder"
                        class="input"
                        @keydown="filterKey"
                        tabindex="0"
                    />
                    <img
                        v-if="newFolder"
                        @keydown="handleClearKey"
                        @click="newFolder = ''"
                        :src=CloseCircle
                        alt="Clear input"
                        aria-label="Clear input"
                        aria-roledescription="button"
                        :tabindex="newFolder.length > 0 ? 0 : -1"
                        class="clear-icon"
                    />
                </span>
                <div class="char-count">
                    {{ newFolder.length }} / 50
                </div>
            </section>
            <section class="buttons" style="flex-direction: row;">
                <button 
                    tabindex="0" 
                    aria-label="Close folder modal" 
                    @keydown="handleCloseKey" 
                    @click="close" 
                    class="buttons-button buttons-cancel"
                    >Cancel
                </button>
                <button
                    tabindex="0"
                    aria-label="Create"
                    @click="handleCreateFolder"
                    @keydown="handleKeyDown"
                    class="buttons-button buttons-create"
                    :disabled="!newFolder.trim() || loading"
                    >
                    <template v-if="loading">
                        Creating...
                    </template>
                    <template v-else>
                        Create
                    </template>
                    </button>
            </section>
        </div>
    </Dialog>
</template>


<script setup lang="ts">
import { ref, onUpdated } from "vue";
import CloseCircle from "../assets/images/CloseCircle.svg";
import { limitCharacter } from "../assets/helpers";
import ErrorBanner from './Modals/ErrorBanner.vue';
import { useStore } from "vuex";

const props = defineProps<{
    visible: boolean; // eslint-disable-next-line
    setVisible: (val: boolean) => void;
}>();

const newFolder = ref(""),
      errorBannerMessage = ref(""),
      showErrorBanner = ref(false),
      loading = ref(false),
      store = useStore();

function filterKey(event: KeyboardEvent) {
    limitCharacter(event, (event.target as HTMLInputElement).value);
}

function close() {
    setshowErrorBanner(false);
    errorBannerMessage.value = "";
    newFolder.value = "";
    loading.value = false;
    props.setVisible(false);
}

function setshowErrorBanner(visible: boolean) {
    showErrorBanner.value = visible;
}

function handleCloseKey(event: KeyboardEvent) {
    if (event.key === 'Enter') {
        event.preventDefault();
        (event.target as HTMLElement).blur();
        close();
    }
}

async function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
        event.preventDefault();
        await handleCreateFolder();
        (event.target as HTMLElement).blur();
        close();
    }
}

function handleClearKey(event: KeyboardEvent) {
    if (event.key === 'Enter') {
        event.preventDefault();
        const input = (event.target as HTMLElement).previousElementSibling as HTMLInputElement;
        newFolder.value = "";
        (event.target as HTMLElement).blur();
        input.focus();
    }
}

async function handleCreateFolder() {
    try {
        if (newFolder.value === "" ) {
            props.setVisible(false);
            return;
        }
        loading.value = true;
        const payload = {
            title: newFolder.value.trim(),
            type: "folder",
        };
        await store.dispatch("itemList/createItem", payload)
        close();
    } catch (error:any) {
        if (error.message.includes("F_NAME_UNIQUE")) {
            errorBannerMessage.value = "Folder title must be unique";
        } else {
            errorBannerMessage.value = "Oops! Something went wrong. Please try again later.";
        }
        setshowErrorBanner(true);
    } finally {
        loading.value = false;
    }
}

// Reset input on dialog update
onUpdated(() => {
    newFolder.value = "";
});
</script>


<style lang="scss">
.create-folder-dialog {
    width: 90vw;
    width: 45.2rem;
    height: auto;
    overflow: hidden;
    
    .p-dialog-header {
        display: none;
    }

    .p-dialog-content{
        background-color: map-get($colours, accent-purple-2);
        border-radius: 1.4rem;
        height: 100%;
        overflow: hidden;
        box-sizing: border-box;
        padding:0;
    }

    .wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        height: 100%; 
        padding:0;
        margin-top: 2.4rem;
        margin-bottom: 3.2rem;
        background-color: map-get($colours, accent-purple-2);
        font-family: $font-secondary;
        color: white;

        .title {
            margin-bottom: 2.4rem;
            font-family: $font-accent;
        }

        .input-wrapper {
        width: 100%;
        padding: 0 3.2rem;
        position: relative;

            .input {
                width:100%;
                height: 4rem;
                color: white;
                padding-right: 3.6rem;
                padding-left: 1.2rem;
                border-radius: 6px;
                border: solid 0.1rem map-get($colours, neutral-set-grey-2);
                background-color: map-get($colours, accent-purple-2);
                box-sizing: border-box;

                &:focus-visible {
                    box-shadow: 0 0 0 0.3rem rgba(255, 233, 254, 0.4);
                    outline: solid 0.2rem rgba(255, 233, 254, 1);
                    border: solid 0.1rem map-get($colours, accent-purple3);
                }
                
                &:hover {
                    border: solid 0.1rem map-get($colours, neutral-set-grey-2);
                }
            }

            .clear-icon {
                position: absolute;
                top: 50%;
                right: 4.4rem;
                transform: translateY(-100%);
                width: 1.6rem;
                height: 1.6rem;
                cursor: pointer;
                pointer-events: auto;
                z-index: 2;
                filter: invert(1);

                &:focus-visible {
                    box-shadow: 0 0 0 0.3rem rgba(255, 233, 254, 0.4);
                    outline: solid 0.2rem rgba(255, 233, 254, 1);
                    border: solid 0.1rem map-get($colours, accent-purple3);
                }
            }
            .char-count{
                text-align: right;
                font-size: 1.2rem;
                color: map-get($colours, neutral-set-grey-3);
                margin-top: 0.4rem;
            }
        }

        .buttons {
            display: flex;
            flex-direction: row;
            justify-content: center;
            flex-wrap: wrap;
            gap: 2.4rem;
            margin-top: 3.2rem;



            .buttons-button {
                width: 16.8rem;
                height: 5.6rem;
                border-radius: 0.8rem;
                font-size: 1.6rem;
                // font-weight: 500;

            &:focus-visible {
                box-shadow: 0 0 0 0.3rem rgba(255, 233, 254, 0.4);
                outline: solid 0.2rem rgba(255, 233, 254, 1);
                border: solid 0.1rem map-get($colours, accent-purple3);
            }
            }

            .buttons-create {
                color: map-get($colours, grey-7);
                background-color: map-get($colours, vibrant-purple);
            }

            .buttons-create:hover {
                background-color: map-get($colours, vibrant-purple-hover);
            }

            .buttons-create:disabled {
                background-color: map-get($colours, grey-3);
                color: map-get($colours, grey-6);
                cursor: not-allowed;
            }

            .buttons-cancel {
                color: map-get($colours, grey-6);
                background-color: map-get($colours, grey-2);
            }
        }
    }
}
</style>
